/**
 * React Query hooks for every API resource.
 *
 * Query keys are arrays rooted at the domain name, so a mutation can
 * invalidate everything under e.g. ["learning"].
 */

import { useEffect } from "react";
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError } from "./client";
import * as api from "./endpoints";
import type * as T from "./types.gen";

type Params<F extends (...args: never[]) => unknown> = Parameters<F>[0];

const MINUTE = 60_000;

/** Shared defaults: no retries on 4xx (they won't succeed), modest staleness. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 2 * MINUTE,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) =>
          !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failureCount < 2,
      },
    },
  });
}

/* ── Seerah ── */

export const useSeerahEvents = (params?: Params<typeof api.seerah.events>) =>
  useQuery({
    queryKey: ["seerah", "events", params],
    queryFn: () => api.seerah.events(params),
    placeholderData: keepPreviousData,
  });

export const useSeerahEvent = (id: string | undefined) =>
  useQuery({ queryKey: ["seerah", "event", id], queryFn: () => api.seerah.event(id!), enabled: !!id });

export const useSeerahFilters = () =>
  useQuery({ queryKey: ["seerah", "filters"], queryFn: api.seerah.filters, staleTime: 30 * MINUTE });

/* ── World history ── */

export const useWorldEvents = (params?: Params<typeof api.worldHistory.events>) =>
  useQuery({
    queryKey: ["world", "events", params],
    queryFn: () => api.worldHistory.events(params),
    placeholderData: keepPreviousData,
  });

export const useWorldEvent = (id: string | undefined) =>
  useQuery({ queryKey: ["world", "event", id], queryFn: () => api.worldHistory.event(id!), enabled: !!id });

export const useParallelTimeline = (params?: Params<typeof api.worldHistory.timeline>) =>
  useQuery({
    queryKey: ["world", "timeline", params],
    queryFn: () => api.worldHistory.timeline(params),
    placeholderData: keepPreviousData,
  });

export const useWorldFilters = () =>
  useQuery({ queryKey: ["world", "filters"], queryFn: api.worldHistory.filters, staleTime: 30 * MINUTE });

/* ── Quran ── */

export const useSurahs = (params?: Params<typeof api.quran.surahs>) =>
  useQuery({ queryKey: ["quran", "surahs", params], queryFn: () => api.quran.surahs(params), staleTime: 60 * MINUTE });

export const useSurah = (number: number | undefined) =>
  useQuery({
    queryKey: ["quran", "surah", number],
    queryFn: () => api.quran.surah(number!),
    enabled: number !== undefined && Number.isInteger(number),
    staleTime: 60 * MINUTE,
  });

export const useAyah = (surah: number | undefined, ayah: number | undefined) =>
  useQuery({
    queryKey: ["quran", "ayah", surah, ayah],
    queryFn: () => api.quran.ayah(surah!, ayah!),
    enabled: surah !== undefined && ayah !== undefined,
    staleTime: 60 * MINUTE,
  });

export const useQuranThemes = () =>
  useQuery({ queryKey: ["quran", "themes"], queryFn: () => api.quran.themes(), staleTime: 60 * MINUTE });

export const useThemeAyahs = (themeId: string | undefined) =>
  useQuery({
    queryKey: ["quran", "theme-ayahs", themeId],
    queryFn: () => api.quran.themeAyahs(themeId!, { page_size: 50 }),
    enabled: !!themeId,
  });

export const useQuranSearch = (query: string) =>
  useQuery({
    queryKey: ["quran", "search", query],
    queryFn: () => api.quran.search({ q: query, page_size: 20 }),
    enabled: query.trim().length >= 2,
  });

export const useQuranFilters = () =>
  useQuery({ queryKey: ["quran", "filters"], queryFn: api.quran.filters, staleTime: 60 * MINUTE });

/* ── Isnad ── */

export const useNarrators = (params?: Params<typeof api.isnad.narrators>) =>
  useQuery({
    queryKey: ["isnad", "narrators", params],
    queryFn: () => api.isnad.narrators(params),
    placeholderData: keepPreviousData,
  });

export const useNarrator = (id: string | undefined) =>
  useQuery({ queryKey: ["isnad", "narrator", id], queryFn: () => api.isnad.narrator(id!), enabled: !!id });

export const useHadiths = (params?: Params<typeof api.isnad.hadiths>) =>
  useQuery({
    queryKey: ["isnad", "hadiths", params],
    queryFn: () => api.isnad.hadiths(params),
    placeholderData: keepPreviousData,
  });

export const useHadith = (id: string | undefined) =>
  useQuery({ queryKey: ["isnad", "hadith", id], queryFn: () => api.isnad.hadith(id!), enabled: !!id });

export const useIsnadFilters = () =>
  useQuery({ queryKey: ["isnad", "filters"], queryFn: api.isnad.filters, staleTime: 30 * MINUTE });

/* ── Clarifier ── */

export const useMisconceptions = (params?: Params<typeof api.clarifier.misconceptions>) =>
  useQuery({
    queryKey: ["clarifier", "list", params],
    queryFn: () => api.clarifier.misconceptions(params),
    placeholderData: keepPreviousData,
  });

export const useMisconception = (id: string | undefined) =>
  useQuery({
    queryKey: ["clarifier", "detail", id],
    queryFn: () => api.clarifier.misconception(id!),
    enabled: !!id,
  });

export const useClarifierFilters = () =>
  useQuery({ queryKey: ["clarifier", "filters"], queryFn: api.clarifier.filters, staleTime: 30 * MINUTE });

export const useClarifierFeedback = () =>
  useMutation({
    mutationFn: ({ id, ...body }: { id: string } & T.FeedbackCreate) => api.clarifier.feedback(id, body),
  });

/** One published version (a permalink); without a version number, the current one. */
export const useMisconceptionVersion = (slug: string | undefined, version: number | undefined) =>
  useQuery({
    queryKey: ["clarifier", "detail", slug, "v", version],
    queryFn: () => api.clarifier.version(slug!, version!),
    enabled: !!slug && !!version,
    staleTime: Infinity, // published versions never change
  });

export const useMisconceptionVersions = (slug: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ["clarifier", "versions", slug],
    queryFn: () => api.clarifier.versions(slug!),
    enabled: !!slug && enabled,
  });

export const useCorrectionsLog = (params?: Params<typeof api.clarifier.corrections>) =>
  useQuery({
    queryKey: ["clarifier", "corrections", params],
    queryFn: () => api.clarifier.corrections(params),
    placeholderData: keepPreviousData,
  });

export const useReportCorrection = () =>
  useMutation({
    mutationFn: ({ id, ...body }: { id: string } & T.CorrectionReportCreate) => api.clarifier.report(id, body),
  });

export const useCorrectionReport = (reportId: string | undefined) =>
  useQuery({
    queryKey: ["clarifier", "report", reportId],
    queryFn: () => api.clarifier.reportStatus(reportId!),
    enabled: !!reportId,
  });

/* ── Scholar review portal ── */

export const useReviewQueue = (enabled = true) =>
  useQuery({ queryKey: ["review", "queue"], queryFn: api.review.queue, enabled, staleTime: 30_000 });

export const useReviewList = (params?: Params<typeof api.review.list>) =>
  useQuery({ queryKey: ["review", "list", params], queryFn: () => api.review.list(params) });

export const useReviewEntry = (id: string | undefined) =>
  useQuery({ queryKey: ["review", "entry", id], queryFn: () => api.review.entry(id!), enabled: !!id });

export const useReviewSettings = () => useQuery({ queryKey: ["review", "settings"], queryFn: api.review.settings });

export const useCorrectionReports = (params?: Params<typeof api.review.reports>, enabled = true) =>
  useQuery({ queryKey: ["review", "reports", params], queryFn: () => api.review.reports(params), enabled });

export const useScholarProfile = (enabled = true) =>
  useQuery({ queryKey: ["review", "profile"], queryFn: api.review.profile, enabled, retry: false });

/** Every review action refreshes the portal and the public clarifier. */
function useReviewMutation<V, R>(mutationFn: (vars: V) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["review"] });
      void queryClient.invalidateQueries({ queryKey: ["clarifier"] });
    },
  });
}

export const useVote = () =>
  useReviewMutation(({ id, ...body }: { id: string } & T.VoteCreate) => api.review.vote(id, body));

export const useUpdateEntry = () =>
  useReviewMutation(({ id, ...body }: { id: string } & T.MisconceptionUpdate) => api.review.update(id, body));

export const useWithdraw = () =>
  useReviewMutation(({ id, ...body }: { id: string } & T.Withdrawal) => api.review.withdraw(id, body));

export const useUpdateReport = () =>
  useReviewMutation(({ reportId, ...body }: { reportId: string } & T.CorrectionReportUpdate) =>
    api.review.updateReport(reportId, body),
  );

export const useChangeStatus = () =>
  useReviewMutation(({ id, ...body }: { id: string } & T.StatusChange) => api.review.changeStatus(id, body));

export const useVerifySource = () =>
  useReviewMutation(({ sourceId, ...body }: { sourceId: string } & T.SourceVerification) =>
    api.review.verifySource(sourceId, body),
  );

export const useGradeSource = () =>
  useReviewMutation(({ sourceId, ...body }: { sourceId: string } & T.ReviewerGradingCreate) =>
    api.review.gradeSource(sourceId, body),
  );

export const useSuggestEdit = () =>
  useReviewMutation(({ id, ...body }: { id: string } & T.SuggestionCreate) => api.review.suggest(id, body));

export const useSuggestSource = () =>
  useReviewMutation(({ id, ...body }: { id: string } & T.SourceSuggestionCreate) => api.review.suggestSource(id, body));

export const useSuggestRemoval = () =>
  useReviewMutation(({ id, ...body }: { id: string } & T.SourceRemovalCreate) => api.review.suggestRemoval(id, body));

export const useDecideSuggestion = () =>
  useReviewMutation(({ suggestionId, ...body }: { suggestionId: string } & T.SuggestionDecision) =>
    api.review.decide(suggestionId, body),
  );

export const useUpdateScholarProfile = () => useReviewMutation(api.review.updateProfile);

export const useUpdateReviewSettings = () => useReviewMutation(api.review.updateSettings);

/* ── Admin: users ── */

export const useAdminUsers = (params?: Params<typeof api.adminUsers.list>) =>
  useQuery({
    queryKey: ["admin", "users", params],
    queryFn: () => api.adminUsers.list(params),
    placeholderData: keepPreviousData,
  });

function useAdminUserMutation<V, R>(mutationFn: (vars: V) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export const useSetUserRole = () =>
  useAdminUserMutation(({ userId, ...body }: { userId: string } & T.UserRoleChange) =>
    api.adminUsers.setRole(userId, body),
  );

export const useInviteScholar = () => useAdminUserMutation(api.adminUsers.invite);

/* ── Learning ── */

export const useLearningPaths = () => useQuery({ queryKey: ["learning", "paths"], queryFn: () => api.learning.paths() });

export const useLearningPath = (id: string | undefined) =>
  useQuery({ queryKey: ["learning", "path", id], queryFn: () => api.learning.path(id!), enabled: !!id });

export const useLesson = (id: string | undefined) =>
  useQuery({ queryKey: ["learning", "lesson", id], queryFn: () => api.learning.lesson(id!), enabled: !!id });

export const useLearningProgress = (enabled = true) =>
  useQuery({ queryKey: ["learning", "progress"], queryFn: api.learning.progress, enabled });

export function useEnroll() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.learning.enroll,
    onSuccess: () => client.invalidateQueries({ queryKey: ["learning"] }),
  });
}

export function useCompleteLesson() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.learning.completeLesson,
    onSuccess: () => client.invalidateQueries({ queryKey: ["learning"] }),
  });
}

/* ── Research Assistant (/scholar) ── */

export const useScholarConversations = (enabled = true) =>
  useQuery({ queryKey: ["scholar", "conversations"], queryFn: () => api.scholar.conversations(), enabled });

export const useScholarConversation = (id: string | undefined) =>
  useQuery({
    queryKey: ["scholar", "conversation", id],
    queryFn: () => api.scholar.conversation(id!),
    enabled: !!id,
  });

export function useAskScholar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.scholar.ask,
    onSuccess: () => client.invalidateQueries({ queryKey: ["scholar", "conversations"] }),
  });
}

export function useDeleteConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.scholar.deleteConversation,
    onSuccess: () => client.invalidateQueries({ queryKey: ["scholar"] }),
  });
}

/* ── Users & bookmarks ── */

export const useCurrentUser = (enabled = true) =>
  useQuery({ queryKey: ["users", "me"], queryFn: api.users.me, enabled, staleTime: 10 * MINUTE });

export const useServerBookmarks = (enabled = true) =>
  useQuery({ queryKey: ["users", "bookmarks"], queryFn: () => api.users.bookmarks({ page_size: 100 }), enabled });

export function useAddBookmark() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.users.addBookmark,
    onSuccess: () => client.invalidateQueries({ queryKey: ["users", "bookmarks"] }),
  });
}

export function useRemoveBookmark() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      api.users.removeBookmark(entityType, entityId),
    onSuccess: () => client.invalidateQueries({ queryKey: ["users", "bookmarks"] }),
  });
}

/* ── Analytics ── */

export const usePopularContent = (params?: Params<typeof api.analytics.popular>) =>
  useQuery({ queryKey: ["analytics", "popular", params], queryFn: () => api.analytics.popular(params) });

export const useDashboard = (days = 30, enabled = true) =>
  useQuery({
    queryKey: ["analytics", "dashboard", days],
    queryFn: () => api.analytics.dashboard({ days }),
    enabled,
    refetchInterval: MINUTE,
  });

export const useHealth = () =>
  useQuery({ queryKey: ["system", "health"], queryFn: api.system.health, staleTime: MINUTE, retry: false });

/* ── Tracking ── */

const recentlyTracked = new Map<string, number>();

let sessionId: string | undefined;

/**
 * A random id for this browser tab, so visitors can be counted without knowing
 * who they are: it is not linked to an account and ends when the tab closes.
 */
function tabSessionId(): string {
  if (sessionId) return sessionId;
  try {
    sessionId = sessionStorage.getItem("dod-session") ?? undefined;
  } catch {
    // Storage blocked (private mode, sandbox): keep an in-memory id instead.
  }
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    try {
      sessionStorage.setItem("dod-session", sessionId);
    } catch {
      // As above.
    }
  }
  return sessionId;
}

/**
 * Fire-and-forget analytics. Never throws; identical events within a second
 * are dropped (React StrictMode runs effects twice in development).
 */
export function track(event: T.TrackEventRequest): void {
  if (import.meta.env.VITE_ENABLE_ANALYTICS === "false") return;
  if (navigator.webdriver) return; // automated browsers, including the deploy-time prerender
  const key = JSON.stringify(event);
  const now = Date.now();
  if (now - (recentlyTracked.get(key) ?? 0) < 1000) return;
  recentlyTracked.set(key, now);
  api.analytics.track({ ...event, sessionId: tabSessionId() }).catch(() => undefined);
}

/** Tracks a view of a page or entity once per mount (and when the entity changes). */
export function usePageView(
  domain: string,
  entity?: { id?: string; type: string; title?: string },
): void {
  const entityId = entity?.id;
  const entityType = entity?.type;
  const title = entity?.title;
  useEffect(() => {
    if (entity && !entityId) return; // wait for the entity to load
    track(
      entityId
        ? { eventType: "view", domain, entityId, entityType, data: title ? { title } : undefined }
        : { eventType: "page_view", domain },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, entityId]);
}
